/**
 * AS PALAVRAS DE UM FLUXO que as funções puras dizem — o nome de cada passo, a
 * espera em tempo, os rótulos de variáveis e saídas.
 *
 * Montado na RAIZ (`shared/i18n`), e não num escopo local, porque quem as pede
 * não é um componente: `flowSteps`, `rule-sentence` e os rótulos do editor são
 * funções chamadas fora do `setup()` e por duas features (a automação e o funil).
 * Um componente com dicionário próprio alcança-as pelo `fallbackRoot`.
 *
 * Inglês é a origem; as chaves vivem todas sob `flowWords` para não esbarrar nas
 * de um escopo local.
 */
export const flowWordsMessages = {
	en: {
		flowWords: {
			/** A field the flow still uses after it was archived (QA 17). */
			archivedField: "{label} (archived)",
			wait: {
				seconds: "wait {n} s",
				minutes: "wait {n} min",
				hours: "wait {n} h",
				days: "wait {n} day | wait {n} days",
			},
			step: {
				"messages.publish": "Publish",
				"messages.send": "Send message",
				"artifacts.run": "Run function",
				"agents.run": "Run mission",
				"agent.ask": "Ask Ben",
				ask: "Understand intent",
				"http.fetch": "Send to another system (webhook)",
				"records.query": "Look up records",
				"records.create": "Create record",
				"records.update": "Change record",
				"contacts.setField": "Change contact",
				"contacts.tag": "Add tag",
				"contacts.untag": "Remove tag",
				"board.place": "Move in funnel",
				"board.remove": "Remove from funnel",
				"conversation.transfer": "Transfer conversation",
				"conversation.note": "Add note",
				"conversation.close": "Close conversation",
				"notify.user": "Notify a person",
				"notify.everyone": "Notify the team",
				action: "Action",
				condition: "Condition",
				wait: "Wait for a reply or a time",
				set: "Set variables",
				end: "Finish",
			},
			outcome: {
				success: "Done",
				received: "Reply",
				timeout: "Time ran out",
				error: "Error",
				ready: "Running",
				waiting: "Waiting",
				done: "Finished",
				failed: "Failed",
				uncertain: "Needs checking",
				otherwise: "Otherwise",
				action: "Action",
				condition: "Condition",
				set: "Variables",
				wait: "Wait",
				end: "End",
			},
			operator: {
				eq: "Is equal to",
				ne: "Is different from",
				contains: "Contains",
				not_contains: "Does not contain",
				gt: "Greater than",
				gte: "Greater than or equal to",
				lt: "Less than",
				lte: "Less than or equal to",
				exists: "Is filled in",
				in: "Is one of these options",
			},
			argument: {
				publication: "Publication",
				function: "Function",
				functionData: "Data for the function",
				mission: "Mission",
				understand: "What to understand",
				answerFormat: "Answer format",
				endpoint: "Endpoint address",
				request: "Request",
				table: "Table",
				filters: "Filters",
				recordData: "Record data",
				record: "Record",
				changes: "Data to change",
				contact: "Contact",
				tag: "Tag",
				field: "Field",
				newValue: "New value",
				note: "Note",
				person: "Person",
				notice: "Notice",
				audience: "Who to tell",
				transferTo: "Where the conversation goes",
				funnelTarget: "Where in the funnel",
			},
			field: {
				template: "Template",
				variables: "Variables",
				language: "Language",
				headerUrl: "Header media",
				headerText: "Header variables",
				fileName: "File name",
				footer: "Footer",
				location: "Location",
				latitude: "Latitude",
				longitude: "Longitude",
				address: "Address",
				name: "Name",
				formattedName: "Contact name",
				phone: "Phone",
				email: "Email",
				cta: "Link button",
				url: "URL",
				askLocation: "Ask for location",
				flow: "Native form",
				screen: "First screen",
				token: "Reply correlation",
				product: "Product",
				catalogId: "Catalog ID",
				productId: "Product ID",
				correlation: "Invitation correlation",
				to: "Recipient",
				text: "Text",
				note: "Remark",
				channel: "Source channel",
				conversation: "Conversation",
				buttons: "Options",
				title: "Title",
				id: "Identifier",
				form: "Form",
				slug: "Form code",
				contact: "Contact",
				mediaUrl: "File or URL",
				mediaKind: "Media type",
				pipeline: "Funnel",
				stage: "Stage",
				objective: "Goal",
				agent: "Assistant",
				queue: "Service queue",
				method: "Method",
				body: "Data sent",
				headers: "Headers",
			},
			variable: {
				contactId: "Contact identifier",
				contactName: "Contact name",
				contactHandle: "Contact phone or address",
				conversationId: "Conversation identifier",
				channelId: "Channel identifier",
				recordId: "Record identifier",
				entityCode: "Data table code",
				stageCode: "Current stage",
				fromStageCode: "Previous stage",
				text: "Message text",
				buttonId: "Chosen option identifier",
				buttonTitle: "Chosen option",
				assigned: "Assigned agent",
				scheduleCode: "Schedule code",
				firedAt: "Fired at",
				first_name: "First name",
				created_at: "Created on",
				external_ref: "External reference",
				tags: "Tags",
				notes: "Notes",
				blocked: "Contact blocked",
				do_not_contact: "Do not contact",
				instagram: "Instagram",
				status: "Status",
				priority: "Priority",
				team: "Team",
				subject: "Subject",
				timezone: "Time zone",
				currency: "Currency",
				country: "Country",
				date: "Date",
				time: "Time",
				datetime: "Date and time",
				iso_date: "Date, sortable",
				weekday: "Day of the week",
				year: "Year",
				intent: "Intent",
				json: "Data",
				result: "Result",
				record: "Record",
				items: "Records found",
				total: "Total",
				amount: "Amount",
				error: "Error",
			},
			group: {
				advanced: "Advanced",
				initialEvent: "Starting event",
				lastReply: "Last reply received",
				contact: "Event contact",
				conversation: "Event conversation",
				workspace: "Your business",
				system: "Event date and time",
				record: "Event record",
				inputs: "Automation inputs",
				form: "Form answered",
				replyOf: "Reply to “{step}”",
				step: "Step “{step}”",
				paths: "Earlier paths",
			},
			label: {
				triggerRecord: "The record that fired the automation",
				executionKey: "Execution identifier",
				visit: "Pass number through the step",
				eventId: "Event identifier",
				eventSource: "Event source",
				errorMessage: "Error message",
				fullResult: "Full result",
				foundCount: "Number of records found",
				foundList: "List of records found",
			},
			path: {
				ofRecord: "{field} of the record",
				previous: "Previous value of {field}",
				resultOf: "Result of {step}",
				totalBy: "Total found by {step}",
				foundIn: "Record found in {step}",
				fieldOf: "{field} of {step}",
			},
			error: {
				noConversation:
					"This trigger did not bring a conversation. Check the message's channel and recipient and save the automation again.",
				noContact:
					"This event did not bring a contact. Link a contact to the card or choose another number as the recipient.",
				missing:
					"A piece of data this step needs is missing: {name}. Check where the field comes from.",
				staticInputs: "Configured inputs need fixed values; use variables in the steps.",
			},
			word: {
				end: "End",
				and: "and",
				conditionN: "Condition {n}",
				empty: "(empty)",
				otherReply: "Another reply",
				sampleText: "Hello",
			},
		},
	},
	"pt-BR": {
		flowWords: {
			archivedField: "{label} (arquivado)",
			wait: {
				seconds: "espera {n} s",
				minutes: "espera {n} min",
				hours: "espera {n} h",
				days: "espera {n} dia | espera {n} dias",
			},
			step: {
				"messages.publish": "Publicar",
				"messages.send": "Enviar mensagem",
				"artifacts.run": "Executar função",
				"agents.run": "Executar missão",
				"agent.ask": "Perguntar ao Ben",
				ask: "Entender intenção",
				"http.fetch": "Enviar para outro sistema (webhook)",
				"records.query": "Consultar registros",
				"records.create": "Criar registro",
				"records.update": "Alterar registro",
				"contacts.setField": "Alterar contato",
				"contacts.tag": "Adicionar etiqueta",
				"contacts.untag": "Remover etiqueta",
				"board.place": "Mover no funil",
				"board.remove": "Tirar do funil",
				"conversation.transfer": "Transferir atendimento",
				"conversation.note": "Adicionar nota",
				"conversation.close": "Encerrar conversa",
				"notify.user": "Avisar pessoa",
				"notify.everyone": "Avisar equipe",
				action: "Ação",
				condition: "Condição",
				wait: "Esperar resposta ou horário",
				set: "Definir variáveis",
				end: "Finalizar",
			},
			outcome: {
				success: "Concluído",
				received: "Resposta",
				timeout: "Prazo esgotado",
				error: "Erro",
				ready: "Em execução",
				waiting: "Aguardando",
				done: "Concluída",
				failed: "Falhou",
				uncertain: "Precisa de conferência",
				otherwise: "Caso contrário",
				action: "Ação",
				condition: "Condição",
				set: "Variáveis",
				wait: "Espera",
				end: "Fim",
			},
			operator: {
				eq: "É igual a",
				ne: "É diferente de",
				contains: "Contém",
				not_contains: "Não contém",
				gt: "Maior que",
				gte: "Maior ou igual a",
				lt: "Menor que",
				lte: "Menor ou igual a",
				exists: "Está preenchido",
				in: "É uma destas opções",
			},
			argument: {
				publication: "Publicação",
				function: "Função",
				functionData: "Dados para a função",
				mission: "Missão",
				understand: "O que entender",
				answerFormat: "Formato da resposta",
				endpoint: "Endereço do endpoint",
				request: "Requisição",
				table: "Tabela",
				filters: "Filtros",
				recordData: "Dados do registro",
				record: "Registro",
				changes: "Dados a alterar",
				contact: "Contato",
				tag: "Etiqueta",
				field: "Campo",
				newValue: "Novo valor",
				note: "Nota",
				person: "Pessoa",
				notice: "Aviso",
				audience: "Para quem",
				transferTo: "Destino do atendimento",
				funnelTarget: "Destino no funil",
			},
			field: {
				template: "Template",
				variables: "Variáveis",
				language: "Idioma",
				headerUrl: "Mídia do cabeçalho",
				headerText: "Variáveis do cabeçalho",
				fileName: "Nome do arquivo",
				footer: "Rodapé",
				location: "Localização",
				latitude: "Latitude",
				longitude: "Longitude",
				address: "Endereço",
				name: "Nome",
				formattedName: "Nome do contato",
				phone: "Telefone",
				email: "E-mail",
				cta: "Botão de link",
				url: "URL",
				askLocation: "Solicitar localização",
				flow: "Formulário nativo",
				screen: "Tela inicial",
				token: "Correlação da resposta",
				product: "Produto",
				catalogId: "ID do catálogo",
				productId: "ID do produto",
				correlation: "Correlação do convite",
				to: "Destinatário",
				text: "Texto",
				note: "Observação",
				channel: "Canal de origem",
				conversation: "Conversa",
				buttons: "Opções",
				title: "Título",
				id: "Identificador",
				form: "Formulário",
				slug: "Código do formulário",
				contact: "Contato",
				mediaUrl: "Arquivo ou URL",
				mediaKind: "Tipo de mídia",
				pipeline: "Funil",
				stage: "Etapa",
				objective: "Objetivo",
				agent: "Assistente",
				queue: "Fila de atendimento",
				method: "Método",
				body: "Dados enviados",
				headers: "Cabeçalhos",
			},
			variable: {
				contactId: "Identificador do contato",
				contactName: "Nome do contato",
				contactHandle: "Telefone ou endereço do contato",
				conversationId: "Identificador da conversa",
				channelId: "Identificador do canal",
				recordId: "Identificador do registro",
				entityCode: "Código da tabela de dados",
				stageCode: "Etapa atual",
				fromStageCode: "Etapa anterior",
				text: "Texto da mensagem",
				buttonId: "Identificador da opção escolhida",
				buttonTitle: "Opção escolhida",
				assigned: "Atendente atribuído",
				scheduleCode: "Código do agendamento",
				firedAt: "Horário do disparo",
				first_name: "Primeiro nome",
				created_at: "Data de criação",
				external_ref: "Referência externa",
				tags: "Etiquetas",
				notes: "Observações",
				blocked: "Contato bloqueado",
				do_not_contact: "Não contatar",
				instagram: "Instagram",
				status: "Situação",
				priority: "Prioridade",
				team: "Equipe",
				subject: "Assunto",
				timezone: "Fuso horário",
				currency: "Moeda",
				country: "País",
				date: "Data",
				time: "Hora",
				datetime: "Data e hora",
				iso_date: "Data, ordenável",
				weekday: "Dia da semana",
				year: "Ano",
				intent: "Intenção",
				json: "Dados",
				result: "Resultado",
				record: "Registro",
				items: "Registros encontrados",
				total: "Total",
				amount: "Valor",
				error: "Erro",
			},
			group: {
				advanced: "Avançado",
				initialEvent: "Evento inicial",
				lastReply: "Última resposta recebida",
				contact: "Contato do evento",
				conversation: "Conversa do evento",
				workspace: "Seu negócio",
				system: "Data e hora do evento",
				record: "Registro do evento",
				inputs: "Entradas da automação",
				form: "Formulário respondido",
				replyOf: "Resposta de “{step}”",
				step: "Etapa “{step}”",
				paths: "Caminhos anteriores",
			},
			label: {
				triggerRecord: "O registro que disparou a automação",
				executionKey: "Identificador da execução",
				visit: "Número da passagem pela etapa",
				eventId: "Identificador do evento",
				eventSource: "Origem do evento",
				errorMessage: "Mensagem de erro",
				fullResult: "Resultado completo",
				foundCount: "Quantidade de registros encontrados",
				foundList: "Lista de registros encontrados",
			},
			path: {
				ofRecord: "{field} do registro",
				previous: "Valor anterior de {field}",
				resultOf: "Resultado de {step}",
				totalBy: "Total encontrado por {step}",
				foundIn: "Registro encontrado em {step}",
				fieldOf: "{field} de {step}",
			},
			error: {
				noConversation:
					"Este gatilho não trouxe uma conversa. Confira o canal e o destinatário da mensagem e salve novamente a automação.",
				noContact:
					"Este evento não trouxe um contato. Vincule um contato ao cartão ou escolha outro número como destinatário.",
				missing: "Falta um dado necessário para esta etapa: {name}. Confira a origem do campo.",
				staticInputs: "Entradas configuradas precisam de valores fixos; use variáveis nos passos.",
			},
			word: {
				end: "Fim",
				and: "e",
				conditionN: "Condição {n}",
				empty: "(vazio)",
				otherReply: "Outra resposta",
				sampleText: "Olá",
			},
		},
	},
	el: {
		flowWords: {
			archivedField: "{label} (αρχειοθετημένο)",
			wait: {
				seconds: "αναμονή {n} δευτ.",
				minutes: "αναμονή {n} λεπτ.",
				hours: "αναμονή {n} ώρ.",
				days: "αναμονή {n} ημέρα | αναμονή {n} ημέρες",
			},
			step: {
				"messages.publish": "Δημοσίευση",
				"messages.send": "Αποστολή μηνύματος",
				"artifacts.run": "Εκτέλεση συνάρτησης",
				"agents.run": "Εκτέλεση αποστολής",
				"agent.ask": "Ερώτηση στον Ben",
				ask: "Κατανόηση πρόθεσης",
				"http.fetch": "Αποστολή σε άλλο σύστημα (webhook)",
				"records.query": "Αναζήτηση εγγραφών",
				"records.create": "Δημιουργία εγγραφής",
				"records.update": "Αλλαγή εγγραφής",
				"contacts.setField": "Αλλαγή επαφής",
				"contacts.tag": "Προσθήκη ετικέτας",
				"contacts.untag": "Αφαίρεση ετικέτας",
				"board.place": "Μετακίνηση στη χοάνη",
				"board.remove": "Αφαίρεση από τη χοάνη",
				"conversation.transfer": "Μεταφορά συνομιλίας",
				"conversation.note": "Προσθήκη σημείωσης",
				"conversation.close": "Κλείσιμο συνομιλίας",
				"notify.user": "Ειδοποίηση ατόμου",
				"notify.everyone": "Ειδοποίηση ομάδας",
				action: "Ενέργεια",
				condition: "Συνθήκη",
				wait: "Αναμονή για απάντηση ή ώρα",
				set: "Ορισμός μεταβλητών",
				end: "Ολοκλήρωση",
			},
			outcome: {
				success: "Ολοκληρώθηκε",
				received: "Απάντηση",
				timeout: "Η προθεσμία έληξε",
				error: "Σφάλμα",
				ready: "Σε εκτέλεση",
				waiting: "Σε αναμονή",
				done: "Ολοκληρώθηκε",
				failed: "Απέτυχε",
				uncertain: "Χρειάζεται έλεγχο",
				otherwise: "Διαφορετικά",
				action: "Ενέργεια",
				condition: "Συνθήκη",
				set: "Μεταβλητές",
				wait: "Αναμονή",
				end: "Τέλος",
			},
			operator: {
				eq: "Ισούται με",
				ne: "Διαφέρει από",
				contains: "Περιέχει",
				not_contains: "Δεν περιέχει",
				gt: "Μεγαλύτερο από",
				gte: "Μεγαλύτερο ή ίσο με",
				lt: "Μικρότερο από",
				lte: "Μικρότερο ή ίσο με",
				exists: "Είναι συμπληρωμένο",
				in: "Είναι μία από αυτές τις επιλογές",
			},
			argument: {
				publication: "Δημοσίευση",
				function: "Συνάρτηση",
				functionData: "Δεδομένα για τη συνάρτηση",
				mission: "Αποστολή",
				understand: "Τι να κατανοήσει",
				answerFormat: "Μορφή απάντησης",
				endpoint: "Διεύθυνση endpoint",
				request: "Αίτημα",
				table: "Πίνακας",
				filters: "Φίλτρα",
				recordData: "Δεδομένα εγγραφής",
				record: "Εγγραφή",
				changes: "Δεδομένα προς αλλαγή",
				contact: "Επαφή",
				tag: "Ετικέτα",
				field: "Πεδίο",
				newValue: "Νέα τιμή",
				note: "Σημείωση",
				person: "Άτομο",
				notice: "Ειδοποίηση",
				audience: "Σε ποιους",
				transferTo: "Προορισμός συνομιλίας",
				funnelTarget: "Προορισμός στη χοάνη",
			},
			field: {
				template: "Πρότυπο",
				variables: "Μεταβλητές",
				language: "Γλώσσα",
				headerUrl: "Πολυμέσα κεφαλίδας",
				headerText: "Μεταβλητές κεφαλίδας",
				fileName: "Όνομα αρχείου",
				footer: "Υποσέλιδο",
				location: "Τοποθεσία",
				latitude: "Γεωγραφικό πλάτος",
				longitude: "Γεωγραφικό μήκος",
				address: "Διεύθυνση",
				name: "Όνομα",
				formattedName: "Όνομα επαφής",
				phone: "Τηλέφωνο",
				email: "Email",
				cta: "Κουμπί συνδέσμου",
				url: "URL",
				askLocation: "Αίτημα τοποθεσίας",
				flow: "Εγγενής φόρμα",
				screen: "Αρχική οθόνη",
				token: "Συσχέτιση απάντησης",
				product: "Προϊόν",
				catalogId: "ID καταλόγου",
				productId: "ID προϊόντος",
				correlation: "Συσχέτιση πρόσκλησης",
				to: "Παραλήπτης",
				text: "Κείμενο",
				note: "Παρατήρηση",
				channel: "Κανάλι προέλευσης",
				conversation: "Συνομιλία",
				buttons: "Επιλογές",
				title: "Τίτλος",
				id: "Αναγνωριστικό",
				form: "Φόρμα",
				slug: "Κωδικός φόρμας",
				contact: "Επαφή",
				mediaUrl: "Αρχείο ή URL",
				mediaKind: "Τύπος πολυμέσων",
				pipeline: "Χοάνη",
				stage: "Στάδιο",
				objective: "Στόχος",
				agent: "Βοηθός",
				queue: "Ουρά εξυπηρέτησης",
				method: "Μέθοδος",
				body: "Απεσταλμένα δεδομένα",
				headers: "Κεφαλίδες",
			},
			variable: {
				contactId: "Αναγνωριστικό επαφής",
				contactName: "Όνομα επαφής",
				contactHandle: "Τηλέφωνο ή διεύθυνση επαφής",
				conversationId: "Αναγνωριστικό συνομιλίας",
				channelId: "Αναγνωριστικό καναλιού",
				recordId: "Αναγνωριστικό εγγραφής",
				entityCode: "Κωδικός πίνακα δεδομένων",
				stageCode: "Τρέχον στάδιο",
				fromStageCode: "Προηγούμενο στάδιο",
				text: "Κείμενο μηνύματος",
				buttonId: "Αναγνωριστικό επιλεγμένης επιλογής",
				buttonTitle: "Επιλεγμένη επιλογή",
				assigned: "Ανατεθειμένος εκπρόσωπος",
				scheduleCode: "Κωδικός προγραμματισμού",
				firedAt: "Ώρα ενεργοποίησης",
				first_name: "Όνομα",
				created_at: "Ημερομηνία δημιουργίας",
				external_ref: "Εξωτερική αναφορά",
				tags: "Ετικέτες",
				notes: "Σημειώσεις",
				blocked: "Αποκλεισμένη επαφή",
				do_not_contact: "Χωρίς επικοινωνία",
				instagram: "Instagram",
				status: "Κατάσταση",
				priority: "Προτεραιότητα",
				team: "Ομάδα",
				subject: "Θέμα",
				timezone: "Ζώνη ώρας",
				currency: "Νόμισμα",
				country: "Χώρα",
				date: "Ημερομηνία",
				time: "Ώρα",
				datetime: "Ημερομηνία και ώρα",
				iso_date: "Ημερομηνία, ταξινομήσιμη",
				weekday: "Ημέρα της εβδομάδας",
				year: "Έτος",
				intent: "Πρόθεση",
				json: "Δεδομένα",
				result: "Αποτέλεσμα",
				record: "Εγγραφή",
				items: "Εγγραφές που βρέθηκαν",
				total: "Σύνολο",
				amount: "Ποσό",
				error: "Σφάλμα",
			},
			group: {
				advanced: "Για προχωρημένους",
				initialEvent: "Αρχικό συμβάν",
				lastReply: "Τελευταία απάντηση που λήφθηκε",
				contact: "Επαφή του συμβάντος",
				conversation: "Συνομιλία του συμβάντος",
				workspace: "Η επιχείρησή σας",
				system: "Ημερομηνία και ώρα του συμβάντος",
				record: "Εγγραφή του συμβάντος",
				inputs: "Είσοδοι του αυτοματισμού",
				form: "Φόρμα που απαντήθηκε",
				replyOf: "Απάντηση στο «{step}»",
				step: "Βήμα «{step}»",
				paths: "Προηγούμενες διαδρομές",
			},
			label: {
				triggerRecord: "Η εγγραφή που ενεργοποίησε τον αυτοματισμό",
				executionKey: "Αναγνωριστικό εκτέλεσης",
				visit: "Αριθμός διέλευσης από το βήμα",
				eventId: "Αναγνωριστικό συμβάντος",
				eventSource: "Προέλευση συμβάντος",
				errorMessage: "Μήνυμα σφάλματος",
				fullResult: "Πλήρες αποτέλεσμα",
				foundCount: "Πλήθος εγγραφών που βρέθηκαν",
				foundList: "Λίστα εγγραφών που βρέθηκαν",
			},
			path: {
				ofRecord: "{field} της εγγραφής",
				previous: "Προηγούμενη τιμή του {field}",
				resultOf: "Αποτέλεσμα του {step}",
				totalBy: "Σύνολο που βρήκε το {step}",
				foundIn: "Εγγραφή που βρέθηκε στο {step}",
				fieldOf: "{field} του {step}",
			},
			error: {
				noConversation:
					"Αυτή η ενεργοποίηση δεν έφερε συνομιλία. Ελέγξτε το κανάλι και τον παραλήπτη του μηνύματος και αποθηκεύστε ξανά τον αυτοματισμό.",
				noContact:
					"Αυτό το συμβάν δεν έφερε επαφή. Συνδέστε μια επαφή στην κάρτα ή επιλέξτε άλλον αριθμό ως παραλήπτη.",
				missing:
					"Λείπει ένα δεδομένο που χρειάζεται αυτό το βήμα: {name}. Ελέγξτε την προέλευση του πεδίου.",
				staticInputs:
					"Οι ρυθμισμένες είσοδοι χρειάζονται σταθερές τιμές· χρησιμοποιήστε μεταβλητές στα βήματα.",
			},
			word: {
				end: "Τέλος",
				and: "και",
				conditionN: "Συνθήκη {n}",
				empty: "(κενό)",
				otherReply: "Άλλη απάντηση",
				sampleText: "Γεια σας",
			},
		},
	},
	es: {
		flowWords: {
			archivedField: "{label} (archivado)",
			wait: {
				seconds: "espera {n} s",
				minutes: "espera {n} min",
				hours: "espera {n} h",
				days: "espera {n} día | espera {n} días",
			},
			step: {
				"messages.publish": "Publicar",
				"messages.send": "Enviar mensaje",
				"artifacts.run": "Ejecutar función",
				"agents.run": "Ejecutar misión",
				"agent.ask": "Preguntar a Ben",
				ask: "Entender intención",
				"http.fetch": "Enviar a otro sistema (webhook)",
				"records.query": "Consultar registros",
				"records.create": "Crear registro",
				"records.update": "Modificar registro",
				"contacts.setField": "Modificar contacto",
				"contacts.tag": "Añadir etiqueta",
				"contacts.untag": "Quitar etiqueta",
				"board.place": "Mover en el embudo",
				"board.remove": "Quitar del embudo",
				"conversation.transfer": "Transferir atención",
				"conversation.note": "Añadir nota",
				"conversation.close": "Cerrar conversación",
				"notify.user": "Avisar a una persona",
				"notify.everyone": "Avisar al equipo",
				action: "Acción",
				condition: "Condición",
				wait: "Esperar respuesta u hora",
				set: "Definir variables",
				end: "Finalizar",
			},
			outcome: {
				success: "Completado",
				received: "Respuesta",
				timeout: "Plazo agotado",
				error: "Error",
				ready: "En ejecución",
				waiting: "Esperando",
				done: "Completada",
				failed: "Falló",
				uncertain: "Necesita revisión",
				otherwise: "En otro caso",
				action: "Acción",
				condition: "Condición",
				set: "Variables",
				wait: "Espera",
				end: "Fin",
			},
			operator: {
				eq: "Es igual a",
				ne: "Es distinto de",
				contains: "Contiene",
				not_contains: "No contiene",
				gt: "Mayor que",
				gte: "Mayor o igual que",
				lt: "Menor que",
				lte: "Menor o igual que",
				exists: "Está rellenado",
				in: "Es una de estas opciones",
			},
			argument: {
				publication: "Publicación",
				function: "Función",
				functionData: "Datos para la función",
				mission: "Misión",
				understand: "Qué entender",
				answerFormat: "Formato de la respuesta",
				endpoint: "Dirección del endpoint",
				request: "Solicitud",
				table: "Tabla",
				filters: "Filtros",
				recordData: "Datos del registro",
				record: "Registro",
				changes: "Datos a modificar",
				contact: "Contacto",
				tag: "Etiqueta",
				field: "Campo",
				newValue: "Nuevo valor",
				note: "Nota",
				person: "Persona",
				notice: "Aviso",
				audience: "Para quién",
				transferTo: "Destino de la atención",
				funnelTarget: "Destino en el embudo",
			},
			field: {
				template: "Plantilla",
				variables: "Variables",
				language: "Idioma",
				headerUrl: "Multimedia del encabezado",
				headerText: "Variables del encabezado",
				fileName: "Nombre del archivo",
				footer: "Pie",
				location: "Ubicación",
				latitude: "Latitud",
				longitude: "Longitud",
				address: "Dirección",
				name: "Nombre",
				formattedName: "Nombre del contacto",
				phone: "Teléfono",
				email: "Correo electrónico",
				cta: "Botón de enlace",
				url: "URL",
				askLocation: "Solicitar ubicación",
				flow: "Formulario nativo",
				screen: "Pantalla inicial",
				token: "Correlación de la respuesta",
				product: "Producto",
				catalogId: "ID del catálogo",
				productId: "ID del producto",
				correlation: "Correlación de la invitación",
				to: "Destinatario",
				text: "Texto",
				note: "Observación",
				channel: "Canal de origen",
				conversation: "Conversación",
				buttons: "Opciones",
				title: "Título",
				id: "Identificador",
				form: "Formulario",
				slug: "Código del formulario",
				contact: "Contacto",
				mediaUrl: "Archivo o URL",
				mediaKind: "Tipo de medio",
				pipeline: "Embudo",
				stage: "Etapa",
				objective: "Objetivo",
				agent: "Asistente",
				queue: "Cola de atención",
				method: "Método",
				body: "Datos enviados",
				headers: "Encabezados",
			},
			variable: {
				contactId: "Identificador del contacto",
				contactName: "Nombre del contacto",
				contactHandle: "Teléfono o dirección del contacto",
				conversationId: "Identificador de la conversación",
				channelId: "Identificador del canal",
				recordId: "Identificador del registro",
				entityCode: "Código de la tabla de datos",
				stageCode: "Etapa actual",
				fromStageCode: "Etapa anterior",
				text: "Texto del mensaje",
				buttonId: "Identificador de la opción elegida",
				buttonTitle: "Opción elegida",
				assigned: "Agente asignado",
				scheduleCode: "Código de la programación",
				firedAt: "Hora del disparo",
				first_name: "Nombre de pila",
				created_at: "Fecha de creación",
				external_ref: "Referencia externa",
				tags: "Etiquetas",
				notes: "Observaciones",
				blocked: "Contacto bloqueado",
				do_not_contact: "No contactar",
				instagram: "Instagram",
				status: "Estado",
				priority: "Prioridad",
				team: "Equipo",
				subject: "Asunto",
				timezone: "Zona horaria",
				currency: "Moneda",
				country: "País",
				date: "Fecha",
				time: "Hora",
				datetime: "Fecha y hora",
				iso_date: "Fecha, ordenable",
				weekday: "Día de la semana",
				year: "Año",
				intent: "Intención",
				json: "Datos",
				result: "Resultado",
				record: "Registro",
				items: "Registros encontrados",
				total: "Total",
				amount: "Importe",
				error: "Error",
			},
			group: {
				advanced: "Avanzado",
				initialEvent: "Evento inicial",
				lastReply: "Última respuesta recibida",
				contact: "Contacto del evento",
				conversation: "Conversación del evento",
				workspace: "Su negocio",
				system: "Fecha y hora del evento",
				record: "Registro del evento",
				inputs: "Entradas de la automatización",
				form: "Formulario respondido",
				replyOf: "Respuesta de “{step}”",
				step: "Paso “{step}”",
				paths: "Caminos anteriores",
			},
			label: {
				triggerRecord: "El registro que disparó la automatización",
				executionKey: "Identificador de la ejecución",
				visit: "Número de paso por la etapa",
				eventId: "Identificador del evento",
				eventSource: "Origen del evento",
				errorMessage: "Mensaje de error",
				fullResult: "Resultado completo",
				foundCount: "Cantidad de registros encontrados",
				foundList: "Lista de registros encontrados",
			},
			path: {
				ofRecord: "{field} del registro",
				previous: "Valor anterior de {field}",
				resultOf: "Resultado de {step}",
				totalBy: "Total encontrado por {step}",
				foundIn: "Registro encontrado en {step}",
				fieldOf: "{field} de {step}",
			},
			error: {
				noConversation:
					"Este disparador no trajo una conversación. Revise el canal y el destinatario del mensaje y guarde de nuevo la automatización.",
				noContact:
					"Este evento no trajo un contacto. Vincule un contacto a la tarjeta o elija otro número como destinatario.",
				missing: "Falta un dato necesario para este paso: {name}. Revise el origen del campo.",
				staticInputs:
					"Las entradas configuradas necesitan valores fijos; use variables en los pasos.",
			},
			word: {
				end: "Fin",
				and: "y",
				conditionN: "Condición {n}",
				empty: "(vacío)",
				otherReply: "Otra respuesta",
				sampleText: "Hola",
			},
		},
	},
	fr: {
		flowWords: {
			archivedField: "{label} (archivé)",
			wait: {
				seconds: "attente {n} s",
				minutes: "attente {n} min",
				hours: "attente {n} h",
				days: "attente {n} jour | attente {n} jours",
			},
			step: {
				"messages.publish": "Publier",
				"messages.send": "Envoyer un message",
				"artifacts.run": "Exécuter une fonction",
				"agents.run": "Exécuter une mission",
				"agent.ask": "Demander à Ben",
				ask: "Comprendre l’intention",
				"http.fetch": "Envoyer vers un autre système (webhook)",
				"records.query": "Rechercher des fiches",
				"records.create": "Créer une fiche",
				"records.update": "Modifier la fiche",
				"contacts.setField": "Modifier le contact",
				"contacts.tag": "Ajouter une étiquette",
				"contacts.untag": "Retirer l’étiquette",
				"board.place": "Déplacer dans l’entonnoir",
				"board.remove": "Retirer de l’entonnoir",
				"conversation.transfer": "Transférer la conversation",
				"conversation.note": "Ajouter une note",
				"conversation.close": "Clore la conversation",
				"notify.user": "Prévenir une personne",
				"notify.everyone": "Prévenir l’équipe",
				action: "Action",
				condition: "Condition",
				wait: "Attendre une réponse ou une heure",
				set: "Définir des variables",
				end: "Terminer",
			},
			outcome: {
				success: "Terminé",
				received: "Réponse",
				timeout: "Délai écoulé",
				error: "Erreur",
				ready: "En cours",
				waiting: "En attente",
				done: "Terminée",
				failed: "Échec",
				uncertain: "À vérifier",
				otherwise: "Sinon",
				action: "Action",
				condition: "Condition",
				set: "Variables",
				wait: "Attente",
				end: "Fin",
			},
			operator: {
				eq: "Est égal à",
				ne: "Est différent de",
				contains: "Contient",
				not_contains: "Ne contient pas",
				gt: "Supérieur à",
				gte: "Supérieur ou égal à",
				lt: "Inférieur à",
				lte: "Inférieur ou égal à",
				exists: "Est renseigné",
				in: "Est l’une de ces options",
			},
			argument: {
				publication: "Publication",
				function: "Fonction",
				functionData: "Données pour la fonction",
				mission: "Mission",
				understand: "Ce qu’il faut comprendre",
				answerFormat: "Format de la réponse",
				endpoint: "Adresse du point de terminaison",
				request: "Requête",
				table: "Table",
				filters: "Filtres",
				recordData: "Données de la fiche",
				record: "Fiche",
				changes: "Données à modifier",
				contact: "Contact",
				tag: "Étiquette",
				field: "Champ",
				newValue: "Nouvelle valeur",
				note: "Note",
				person: "Personne",
				notice: "Avis",
				audience: "Pour qui",
				transferTo: "Destination de la conversation",
				funnelTarget: "Destination dans l’entonnoir",
			},
			field: {
				template: "Modèle",
				variables: "Variables",
				language: "Langue",
				headerUrl: "Média de l’en-tête",
				headerText: "Variables de l’en-tête",
				fileName: "Nom du fichier",
				footer: "Pied de page",
				location: "Emplacement",
				latitude: "Latitude",
				longitude: "Longitude",
				address: "Adresse",
				name: "Nom",
				formattedName: "Nom du contact",
				phone: "Téléphone",
				email: "E-mail",
				cta: "Bouton de lien",
				url: "URL",
				askLocation: "Demander la position",
				flow: "Formulaire natif",
				screen: "Écran initial",
				token: "Corrélation de la réponse",
				product: "Produit",
				catalogId: "ID du catalogue",
				productId: "ID du produit",
				correlation: "Corrélation de l’invitation",
				to: "Destinataire",
				text: "Texte",
				note: "Remarque",
				channel: "Canal d’origine",
				conversation: "Conversation",
				buttons: "Options",
				title: "Titre",
				id: "Identifiant",
				form: "Formulaire",
				slug: "Code du formulaire",
				contact: "Contact",
				mediaUrl: "Fichier ou URL",
				mediaKind: "Type de média",
				pipeline: "Entonnoir",
				stage: "Étape",
				objective: "Objectif",
				agent: "Assistant",
				queue: "File d’attente",
				method: "Méthode",
				body: "Données envoyées",
				headers: "En-têtes",
			},
			variable: {
				contactId: "Identifiant du contact",
				contactName: "Nom du contact",
				contactHandle: "Téléphone ou adresse du contact",
				conversationId: "Identifiant de la conversation",
				channelId: "Identifiant du canal",
				recordId: "Identifiant de la fiche",
				entityCode: "Code de la table de données",
				stageCode: "Étape actuelle",
				fromStageCode: "Étape précédente",
				text: "Texte du message",
				buttonId: "Identifiant de l’option choisie",
				buttonTitle: "Option choisie",
				assigned: "Agent assigné",
				scheduleCode: "Code de la planification",
				firedAt: "Heure du déclenchement",
				first_name: "Prénom",
				created_at: "Date de création",
				external_ref: "Référence externe",
				tags: "Étiquettes",
				notes: "Remarques",
				blocked: "Contact bloqué",
				do_not_contact: "Ne pas contacter",
				instagram: "Instagram",
				status: "Statut",
				priority: "Priorité",
				team: "Équipe",
				subject: "Sujet",
				timezone: "Fuseau horaire",
				currency: "Devise",
				country: "Pays",
				date: "Date",
				time: "Heure",
				datetime: "Date et heure",
				iso_date: "Date, triable",
				weekday: "Jour de la semaine",
				year: "Année",
				intent: "Intention",
				json: "Données",
				result: "Résultat",
				record: "Fiche",
				items: "Fiches trouvées",
				total: "Total",
				amount: "Montant",
				error: "Erreur",
			},
			group: {
				advanced: "Avancé",
				initialEvent: "Événement initial",
				lastReply: "Dernière réponse reçue",
				contact: "Contact de l’événement",
				conversation: "Conversation de l’événement",
				workspace: "Votre entreprise",
				system: "Date et heure de l’événement",
				record: "Fiche de l’événement",
				inputs: "Entrées de l’automatisation",
				form: "Formulaire rempli",
				replyOf: "Réponse à « {step} »",
				step: "Étape « {step} »",
				paths: "Chemins précédents",
			},
			label: {
				triggerRecord: "La fiche qui a déclenché l’automatisation",
				executionKey: "Identifiant de l’exécution",
				visit: "Numéro de passage dans l’étape",
				eventId: "Identifiant de l’événement",
				eventSource: "Origine de l’événement",
				errorMessage: "Message d’erreur",
				fullResult: "Résultat complet",
				foundCount: "Nombre de fiches trouvées",
				foundList: "Liste des fiches trouvées",
			},
			path: {
				ofRecord: "{field} de la fiche",
				previous: "Valeur précédente de {field}",
				resultOf: "Résultat de {step}",
				totalBy: "Total trouvé par {step}",
				foundIn: "Fiche trouvée dans {step}",
				fieldOf: "{field} de {step}",
			},
			error: {
				noConversation:
					"Ce déclencheur n’a pas apporté de conversation. Vérifiez le canal et le destinataire du message, puis enregistrez de nouveau l’automatisation.",
				noContact:
					"Cet événement n’a pas apporté de contact. Liez un contact à la carte ou choisissez un autre numéro comme destinataire.",
				missing:
					"Il manque une donnée nécessaire à cette étape : {name}. Vérifiez l’origine du champ.",
				staticInputs:
					"Les entrées configurées exigent des valeurs fixes ; utilisez des variables dans les étapes.",
			},
			word: {
				end: "Fin",
				and: "et",
				conditionN: "Condition {n}",
				empty: "(vide)",
				otherReply: "Autre réponse",
				sampleText: "Bonjour",
			},
		},
	},
	it: {
		flowWords: {
			archivedField: "{label} (archiviato)",
			wait: {
				seconds: "attesa {n} s",
				minutes: "attesa {n} min",
				hours: "attesa {n} h",
				days: "attesa {n} giorno | attesa {n} giorni",
			},
			step: {
				"messages.publish": "Pubblica",
				"messages.send": "Invia messaggio",
				"artifacts.run": "Esegui funzione",
				"agents.run": "Esegui missione",
				"agent.ask": "Chiedi a Ben",
				ask: "Capire l’intenzione",
				"http.fetch": "Invia a un altro sistema (webhook)",
				"records.query": "Cerca record",
				"records.create": "Crea record",
				"records.update": "Modifica record",
				"contacts.setField": "Modifica contatto",
				"contacts.tag": "Aggiungi etichetta",
				"contacts.untag": "Rimuovi etichetta",
				"board.place": "Sposta nell’imbuto",
				"board.remove": "Togli dall’imbuto",
				"conversation.transfer": "Trasferisci conversazione",
				"conversation.note": "Aggiungi nota",
				"conversation.close": "Chiudi conversazione",
				"notify.user": "Avvisa una persona",
				"notify.everyone": "Avvisa il team",
				action: "Azione",
				condition: "Condizione",
				wait: "Attendi risposta o orario",
				set: "Imposta variabili",
				end: "Termina",
			},
			outcome: {
				success: "Completato",
				received: "Risposta",
				timeout: "Tempo scaduto",
				error: "Errore",
				ready: "In esecuzione",
				waiting: "In attesa",
				done: "Completata",
				failed: "Non riuscita",
				uncertain: "Da verificare",
				otherwise: "Altrimenti",
				action: "Azione",
				condition: "Condizione",
				set: "Variabili",
				wait: "Attesa",
				end: "Fine",
			},
			operator: {
				eq: "È uguale a",
				ne: "È diverso da",
				contains: "Contiene",
				not_contains: "Non contiene",
				gt: "Maggiore di",
				gte: "Maggiore o uguale a",
				lt: "Minore di",
				lte: "Minore o uguale a",
				exists: "È compilato",
				in: "È una di queste opzioni",
			},
			argument: {
				publication: "Pubblicazione",
				function: "Funzione",
				functionData: "Dati per la funzione",
				mission: "Missione",
				understand: "Cosa capire",
				answerFormat: "Formato della risposta",
				endpoint: "Indirizzo dell’endpoint",
				request: "Richiesta",
				table: "Tabella",
				filters: "Filtri",
				recordData: "Dati del record",
				record: "Record",
				changes: "Dati da modificare",
				contact: "Contatto",
				tag: "Etichetta",
				field: "Campo",
				newValue: "Nuovo valore",
				note: "Nota",
				person: "Persona",
				notice: "Avviso",
				audience: "Per chi",
				transferTo: "Destinazione della conversazione",
				funnelTarget: "Destinazione nell’imbuto",
			},
			field: {
				template: "Modello",
				variables: "Variabili",
				language: "Lingua",
				headerUrl: "Media dell’intestazione",
				headerText: "Variabili dell’intestazione",
				fileName: "Nome del file",
				footer: "Piè di pagina",
				location: "Posizione",
				latitude: "Latitudine",
				longitude: "Longitudine",
				address: "Indirizzo",
				name: "Nome",
				formattedName: "Nome del contatto",
				phone: "Telefono",
				email: "E-mail",
				cta: "Pulsante link",
				url: "URL",
				askLocation: "Chiedi la posizione",
				flow: "Modulo nativo",
				screen: "Schermata iniziale",
				token: "Correlazione della risposta",
				product: "Prodotto",
				catalogId: "ID del catalogo",
				productId: "ID del prodotto",
				correlation: "Correlazione dell’invito",
				to: "Destinatario",
				text: "Testo",
				note: "Osservazione",
				channel: "Canale di origine",
				conversation: "Conversazione",
				buttons: "Opzioni",
				title: "Titolo",
				id: "Identificativo",
				form: "Modulo",
				slug: "Codice del modulo",
				contact: "Contatto",
				mediaUrl: "File o URL",
				mediaKind: "Tipo di media",
				pipeline: "Imbuto",
				stage: "Fase",
				objective: "Obiettivo",
				agent: "Assistente",
				queue: "Coda di assistenza",
				method: "Metodo",
				body: "Dati inviati",
				headers: "Intestazioni",
			},
			variable: {
				contactId: "Identificativo del contatto",
				contactName: "Nome del contatto",
				contactHandle: "Telefono o indirizzo del contatto",
				conversationId: "Identificativo della conversazione",
				channelId: "Identificativo del canale",
				recordId: "Identificativo del record",
				entityCode: "Codice della tabella dati",
				stageCode: "Fase attuale",
				fromStageCode: "Fase precedente",
				text: "Testo del messaggio",
				buttonId: "Identificativo dell’opzione scelta",
				buttonTitle: "Opzione scelta",
				assigned: "Operatore assegnato",
				scheduleCode: "Codice della pianificazione",
				firedAt: "Ora dell’attivazione",
				first_name: "Nome di battesimo",
				created_at: "Data di creazione",
				external_ref: "Riferimento esterno",
				tags: "Etichette",
				notes: "Note",
				blocked: "Contatto bloccato",
				do_not_contact: "Non contattare",
				instagram: "Instagram",
				status: "Stato",
				priority: "Priorità",
				team: "Team",
				subject: "Oggetto",
				timezone: "Fuso orario",
				currency: "Valuta",
				country: "Paese",
				date: "Data",
				time: "Ora",
				datetime: "Data e ora",
				iso_date: "Data, ordinabile",
				weekday: "Giorno della settimana",
				year: "Anno",
				intent: "Intenzione",
				json: "Dati",
				result: "Risultato",
				record: "Record",
				items: "Record trovati",
				total: "Totale",
				amount: "Importo",
				error: "Errore",
			},
			group: {
				advanced: "Avanzate",
				initialEvent: "Evento iniziale",
				lastReply: "Ultima risposta ricevuta",
				contact: "Contatto dell’evento",
				conversation: "Conversazione dell’evento",
				workspace: "La tua attività",
				system: "Data e ora dell’evento",
				record: "Record dell’evento",
				inputs: "Input dell’automazione",
				form: "Modulo compilato",
				replyOf: "Risposta a “{step}”",
				step: "Passo “{step}”",
				paths: "Percorsi precedenti",
			},
			label: {
				triggerRecord: "Il record che ha attivato l’automazione",
				executionKey: "Identificativo dell’esecuzione",
				visit: "Numero di passaggio nel passo",
				eventId: "Identificativo dell’evento",
				eventSource: "Origine dell’evento",
				errorMessage: "Messaggio di errore",
				fullResult: "Risultato completo",
				foundCount: "Numero di record trovati",
				foundList: "Elenco dei record trovati",
			},
			path: {
				ofRecord: "{field} del record",
				previous: "Valore precedente di {field}",
				resultOf: "Risultato di {step}",
				totalBy: "Totale trovato da {step}",
				foundIn: "Record trovato in {step}",
				fieldOf: "{field} di {step}",
			},
			error: {
				noConversation:
					"Questo attivatore non ha portato una conversazione. Controlla il canale e il destinatario del messaggio e salva di nuovo l’automazione.",
				noContact:
					"Questo evento non ha portato un contatto. Collega un contatto alla scheda o scegli un altro numero come destinatario.",
				missing:
					"Manca un dato necessario per questo passo: {name}. Controlla l’origine del campo.",
				staticInputs: "Gli input configurati richiedono valori fissi; usa le variabili nei passi.",
			},
			word: {
				end: "Fine",
				and: "e",
				conditionN: "Condizione {n}",
				empty: "(vuoto)",
				otherReply: "Altra risposta",
				sampleText: "Ciao",
			},
		},
	},
	de: {
		flowWords: {
			archivedField: "{label} (archiviert)",
			wait: {
				seconds: "{n} s warten",
				minutes: "{n} min warten",
				hours: "{n} h warten",
				days: "{n} Tag warten | {n} Tage warten",
			},
			step: {
				"messages.publish": "Veröffentlichen",
				"messages.send": "Nachricht senden",
				"artifacts.run": "Funktion ausführen",
				"agents.run": "Mission ausführen",
				"agent.ask": "Ben fragen",
				ask: "Absicht verstehen",
				"http.fetch": "An anderes System senden (Webhook)",
				"records.query": "Datensätze abfragen",
				"records.create": "Datensatz anlegen",
				"records.update": "Datensatz ändern",
				"contacts.setField": "Kontakt ändern",
				"contacts.tag": "Tag hinzufügen",
				"contacts.untag": "Tag entfernen",
				"board.place": "Im Funnel verschieben",
				"board.remove": "Aus dem Funnel entfernen",
				"conversation.transfer": "Gespräch übergeben",
				"conversation.note": "Notiz hinzufügen",
				"conversation.close": "Gespräch schließen",
				"notify.user": "Person benachrichtigen",
				"notify.everyone": "Team benachrichtigen",
				action: "Aktion",
				condition: "Bedingung",
				wait: "Auf Antwort oder Zeit warten",
				set: "Variablen setzen",
				end: "Beenden",
			},
			outcome: {
				success: "Erledigt",
				received: "Antwort",
				timeout: "Frist abgelaufen",
				error: "Fehler",
				ready: "Läuft",
				waiting: "Wartet",
				done: "Abgeschlossen",
				failed: "Fehlgeschlagen",
				uncertain: "Prüfung nötig",
				otherwise: "Andernfalls",
				action: "Aktion",
				condition: "Bedingung",
				set: "Variablen",
				wait: "Warten",
				end: "Ende",
			},
			operator: {
				eq: "Ist gleich",
				ne: "Ist ungleich",
				contains: "Enthält",
				not_contains: "Enthält nicht",
				gt: "Größer als",
				gte: "Größer oder gleich",
				lt: "Kleiner als",
				lte: "Kleiner oder gleich",
				exists: "Ist ausgefüllt",
				in: "Ist eine dieser Optionen",
			},
			argument: {
				publication: "Veröffentlichung",
				function: "Funktion",
				functionData: "Daten für die Funktion",
				mission: "Mission",
				understand: "Was zu verstehen ist",
				answerFormat: "Antwortformat",
				endpoint: "Endpoint-Adresse",
				request: "Anfrage",
				table: "Tabelle",
				filters: "Filter",
				recordData: "Datensatzdaten",
				record: "Datensatz",
				changes: "Zu ändernde Daten",
				contact: "Kontakt",
				tag: "Tag",
				field: "Feld",
				newValue: "Neuer Wert",
				note: "Notiz",
				person: "Person",
				notice: "Hinweis",
				audience: "Für wen",
				transferTo: "Ziel des Gesprächs",
				funnelTarget: "Ziel im Funnel",
			},
			field: {
				template: "Vorlage",
				variables: "Variablen",
				language: "Sprache",
				headerUrl: "Kopfzeilen-Medium",
				headerText: "Kopfzeilen-Variablen",
				fileName: "Dateiname",
				footer: "Fußzeile",
				location: "Standort",
				latitude: "Breitengrad",
				longitude: "Längengrad",
				address: "Adresse",
				name: "Name",
				formattedName: "Kontaktname",
				phone: "Telefon",
				email: "E-Mail",
				cta: "Link-Schaltfläche",
				url: "URL",
				askLocation: "Standort anfragen",
				flow: "Natives Formular",
				screen: "Startbildschirm",
				token: "Antwortkorrelation",
				product: "Produkt",
				catalogId: "Katalog-ID",
				productId: "Produkt-ID",
				correlation: "Einladungskorrelation",
				to: "Empfänger",
				text: "Text",
				note: "Bemerkung",
				channel: "Quellkanal",
				conversation: "Gespräch",
				buttons: "Optionen",
				title: "Titel",
				id: "Kennung",
				form: "Formular",
				slug: "Formularcode",
				contact: "Kontakt",
				mediaUrl: "Datei oder URL",
				mediaKind: "Medientyp",
				pipeline: "Funnel",
				stage: "Phase",
				objective: "Ziel",
				agent: "Assistent",
				queue: "Warteschlange",
				method: "Methode",
				body: "Gesendete Daten",
				headers: "Header",
			},
			variable: {
				contactId: "Kontakt-Kennung",
				contactName: "Kontaktname",
				contactHandle: "Telefon oder Adresse des Kontakts",
				conversationId: "Gesprächs-Kennung",
				channelId: "Kanal-Kennung",
				recordId: "Datensatz-Kennung",
				entityCode: "Code der Datentabelle",
				stageCode: "Aktuelle Phase",
				fromStageCode: "Vorherige Phase",
				text: "Nachrichtentext",
				buttonId: "Kennung der gewählten Option",
				buttonTitle: "Gewählte Option",
				assigned: "Zugewiesener Bearbeiter",
				scheduleCode: "Zeitplan-Code",
				firedAt: "Auslösezeit",
				first_name: "Vorname",
				created_at: "Erstellt am",
				external_ref: "Externe Referenz",
				tags: "Tags",
				notes: "Notizen",
				blocked: "Kontakt blockiert",
				do_not_contact: "Nicht kontaktieren",
				instagram: "Instagram",
				status: "Status",
				priority: "Priorität",
				team: "Team",
				subject: "Betreff",
				timezone: "Zeitzone",
				currency: "Währung",
				country: "Land",
				date: "Datum",
				time: "Uhrzeit",
				datetime: "Datum und Uhrzeit",
				iso_date: "Datum, sortierbar",
				weekday: "Wochentag",
				year: "Jahr",
				intent: "Absicht",
				json: "Daten",
				result: "Ergebnis",
				record: "Datensatz",
				items: "Gefundene Datensätze",
				total: "Summe",
				amount: "Betrag",
				error: "Fehler",
			},
			group: {
				advanced: "Erweitert",
				initialEvent: "Startereignis",
				lastReply: "Zuletzt erhaltene Antwort",
				contact: "Kontakt des Ereignisses",
				conversation: "Gespräch des Ereignisses",
				workspace: "Ihr Unternehmen",
				system: "Datum und Uhrzeit des Ereignisses",
				record: "Datensatz des Ereignisses",
				inputs: "Eingaben der Automatisierung",
				form: "Beantwortetes Formular",
				replyOf: "Antwort auf „{step}“",
				step: "Schritt „{step}“",
				paths: "Frühere Pfade",
			},
			label: {
				triggerRecord: "Der Datensatz, der die Automatisierung ausgelöst hat",
				executionKey: "Ausführungs-Kennung",
				visit: "Durchlaufnummer im Schritt",
				eventId: "Ereignis-Kennung",
				eventSource: "Ereignisquelle",
				errorMessage: "Fehlermeldung",
				fullResult: "Vollständiges Ergebnis",
				foundCount: "Anzahl gefundener Datensätze",
				foundList: "Liste gefundener Datensätze",
			},
			path: {
				ofRecord: "{field} des Datensatzes",
				previous: "Vorheriger Wert von {field}",
				resultOf: "Ergebnis von {step}",
				totalBy: "Von {step} gefundene Summe",
				foundIn: "In {step} gefundener Datensatz",
				fieldOf: "{field} von {step}",
			},
			error: {
				noConversation:
					"Dieser Auslöser hat kein Gespräch mitgebracht. Prüfen Sie Kanal und Empfänger der Nachricht und speichern Sie die Automatisierung erneut.",
				noContact:
					"Dieses Ereignis hat keinen Kontakt mitgebracht. Verknüpfen Sie einen Kontakt mit der Karte oder wählen Sie eine andere Nummer als Empfänger.",
				missing:
					"Für diesen Schritt fehlt eine nötige Angabe: {name}. Prüfen Sie die Herkunft des Feldes.",
				staticInputs:
					"Konfigurierte Eingaben brauchen feste Werte; verwenden Sie Variablen in den Schritten.",
			},
			word: {
				end: "Ende",
				and: "und",
				conditionN: "Bedingung {n}",
				empty: "(leer)",
				otherReply: "Andere Antwort",
				sampleText: "Hallo",
			},
		},
	},
} as const;
